import React, { useState, useEffect } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Copy, ExternalLink, Check, Loader2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const PROVIDER_LABEL = {
  wa_me: "wa.me (Manual)",
  fonnte: "Fonnte",
  wablas: "Wablas",
  watzap: "Watzap",
  twilio: "Twilio",
};

export const WhatsAppReminderModal = ({ invoice, type = "tagihan", onClose }) => {
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");
  const [provider, setProvider] = useState("wa_me");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok: bool, text: string }

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.post(
          `${BACKEND_URL}/api/whatsapp/preview`,
          { tagihan_id: invoice.id || invoice._id, type },
          { withCredentials: true }
        );
        if (!active) return;
        setMessage(res.data.message);
        setProvider(res.data.provider);
        setPhone(res.data.phone);
      } catch (e) {
        if (active) setResult({ ok: false, text: e.response?.data?.detail || "Gagal memuat template pesan" });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [invoice, type]);

  const waUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/whatsapp/send`,
        { tagihan_id: invoice.id || invoice._id, type, message },
        { withCredentials: true }
      );
      if (res.data.mode === "manual") {
        window.open(res.data.wa_url, "_blank");
        setResult({ ok: true, text: "Provider wa.me aktif — WhatsApp dibuka untuk kirim manual." });
      } else {
        setResult({ ok: true, text: `Pesan terkirim via ${PROVIDER_LABEL[res.data.provider] || res.data.provider} ke ${res.data.phone}.` });
      }
    } catch (e) {
      setResult({ ok: false, text: e.response?.data?.detail || "Gagal mengirim pesan WhatsApp" });
    } finally {
      setSending(false);
    }
  };

  if (!invoice) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="wa-modal">
      <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800 bg-[#090f1d]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white" data-testid="wa-modal-title">{type === "lunas" ? "Kirim Konfirmasi Tagihan Lunas" : "Kirim Pengingat Tagihan WhatsApp"}</h3>
              <p className="text-[11px] text-slate-400">
                {invoice.pelanggan_nama} ({phone || invoice.pelanggan_no_hp}) · Provider:{" "}
                <span className="text-emerald-400 font-semibold" data-testid="wa-provider-label">{PROVIDER_LABEL[provider] || provider}</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-md" data-testid="wa-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400 text-xs gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Memuat template pesan...
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                Isi Pesan (variabel sudah otomatis terisi — boleh diedit sebelum kirim):
              </label>
              <textarea
                rows={11}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="wa-message-textarea"
                className="w-full bg-[#090f1d] border border-slate-800 rounded-xl p-3.5 text-xs text-slate-200 font-sans whitespace-pre-line leading-relaxed focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          )}

          {result && (
            <div className={`p-3 rounded-xl text-xs flex items-center gap-2 border ${result.ok ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border-rose-500/30 text-rose-300"}`} data-testid="wa-result">
              {result.ok ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              <span>{result.text}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 bg-[#0d1527] border-t border-slate-800 flex flex-wrap gap-2.5 justify-end">
          <button
            onClick={handleCopy}
            className="py-2 px-3 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
            data-testid="wa-copy-btn"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? "Tersalin!" : "Salin"}</span>
          </button>

          <button
            onClick={handleSend}
            disabled={sending || loading}
            data-testid="wa-gateway-send-btn"
            className="py-2 px-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition-colors"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            <span>{provider === "wa_me" ? "Kirim (Manual wa.me)" : `Kirim via ${PROVIDER_LABEL[provider] || "Gateway"}`}</span>
          </button>

          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-wa-chat-btn"
            className="py-2 px-4 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all shadow-lg shadow-emerald-600/30"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>Buka WhatsApp</span>
          </a>
        </div>
      </div>
    </div>
  );
};
